import React from 'react';
import { useNavigate } from 'react-router-dom';
import storyBoardVideo from '../assets/003.mp4';
import dimensionalGateStill from '../assets/0003.jpg';
import dimensionalGateOfflineStill from '../assets/0004.jpg';
import dimensionalGateOfflineStillAlt from '../assets/0005.jpg';
import dimensionalGateOfflinePhoto from '../assets/次元之门线下.jpg';
import { projectDetailThemeStyles } from '../projectThemes';
import './ProjectDetailLayout.css';
import './DimensionalGateProject.css';

export const DimensionalGateProject: React.FC = () => {
  const navigate = useNavigate();
  return (
  <main
    className="project-detail-page dimensional-gate-project-page"
    style={projectDetailThemeStyles.dimensionalGate}
  >
    <aside className="project-page__sidebar">
      <button type="button" className="project-page__back" onClick={() => navigate(-1)}>
        <span className="project-page__back-arrow" aria-hidden="true">
          &lt;-
        </span>
        back to showcase
      </button>
      <div className="project-page__sidebar-summary">
        <p className="project-page__eyebrow">advertising work / campaign concept</p>
        <h1
          className="project-page__title"
          aria-label="Tmall IP Marketing Campaign for Gen-Z Engagement"
        >
          Dimensional Gate
        </h1>
        <p className="project-page__description">
          A campaign idea connecting virtual IP worlds with real-life New Year experiences for
          Gen-Z audiences.
        </p>
        <div className="project-page__tech-list" aria-label="Project disciplines">
          <span>Advertising creative</span>
          <span>Brand campaign</span>
          <span>IP marketing</span>
        </div>
      </div>
    </aside>

    <section className="project-page__content" aria-label="Project content">
      <div className="project-page__media">
        <video
          className="project-page__video"
          src={storyBoardVideo}
          autoPlay
          loop
          muted
          playsInline
        />
      </div>
      <div className="project-page__section">
        <p className="project-page__section-kicker">creative idea / case study</p>
        <h2 className="project-page__section-title">
          A gateway between IP worlds and everyday life.
        </h2>
        <div className="project-page__section-body">
          <p>
            In this project, I participated in developing the core creative concept
            "Dimensional Gate" for Tmall Tonghao Club's Chinese New Year campaign. The
            objective of the campaign was to engage Gen Z audiences through
            youth-oriented marketing and leverage anime and gaming IP culture to
            create a new narrative around the Chinese New Year shopping season.
          </p>
          <p>
            During the strategy phase, we identified a key challenge: as a platform
            brand, Tmall does not own a single IP, so the campaign could not focus too
            heavily on any individual game or anime property. Instead, it needed to
            highlight Tmall's role as a platform that connects different IPs with
            consumers.
          </p>
          <p>
            Based on this insight, I developed the creative idea of "Dimensional Gate."
            The concept positions Tmall as a gateway between the virtual world of anime
            and gaming IPs and the real world. Through this "gate," characters and
            elements from different IP universes could metaphorically cross dimensions
            and appear in the real world, interacting with consumers.
          </p>
          <figure className="project-page__inline-figure project-page__inline-figure--small">
            <img
              className="project-page__inline-image"
              src={dimensionalGateStill}
              alt="Dimensional Gate campaign visual"
            />
          </figure>
          <p>
            Building on this concept, I constructed a broader narrative for the
            campaign: once the Dimensional Gate opens, characters from various IP worlds
            arrive in reality to bring good fortune for the New Year. This framework
            allowed us to integrate multiple IPs within one cohesive story while
            naturally positioning Tmall as the platform that opens the gateway between
            worlds.
          </p>
          <p>
            In execution, the concept brought together 30+ anime and gaming IPs and was
            communicated through a combination of online and offline channels to
            visualize the idea of "crossing dimensions." The campaign also connected
            with the New Year theme of seeking good fortune, promoting a New Year lucky
            bag product that sparked conversations among younger audiences on social
            media.
          </p>
          <figure className="project-page__inline-figure project-page__inline-figure--small">
            <img
              className="project-page__inline-image"
              src={dimensionalGateOfflineStill}
              alt="Dimensional Gate campaign activation"
            />
          </figure>
          <figure className="project-page__inline-figure project-page__inline-figure--small">
            <img
              className="project-page__inline-image"
              src={dimensionalGateOfflineStillAlt}
              alt="Dimensional Gate campaign installation detail"
            />
          </figure>
          <p>
            Ultimately, the "Dimensional Gate" concept became the central narrative and
            visual theme of the campaign, and was applied to the brand film and offline
            campaign materials, helping Tmall connect IP culture with consumer
            experiences in a more youth-oriented way.
          </p>
          <figure className="project-page__inline-figure project-page__inline-figure--small">
            <img
              className="project-page__inline-image"
              src={dimensionalGateOfflinePhoto}
              alt="Dimensional Gate offline activation"
            />
          </figure>
          <p>
            In this project, I was mainly responsible for developing the core creative
            concept and the early narrative framework of the campaign.
          </p>
        </div>
      </div>
    </section>
  </main>
  );
};
